import { Table, TableSplit } from '@carbon/icons-react';
import { ConfigProvider, Flex, Switch } from 'antd';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FORMAT_DATE, WIDTH_COLUMN } from '../../const';
import { getStartOfWeek, getWidthColumn } from '../../functions';
import useWidthGant from '../../hooks/useWidthGant';
import { useRouting } from '../../routing/routingProvider';
import { ComponentsDispatch, RootState } from '../../store';
import { setterGantScroll } from '../../store/slices/gantScrollSlice';
import { setterGant } from '../../store/slices/gantSlice';
import { HeaderType, ModeTimeLineType } from '../../types';

const SwitchTimeLine = () => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const widthScreenForGant = useWidthGant();

    const modeTimeLine = useSelector<RootState, ModeTimeLineType>((state) => state.gant.modeTimeLine);
    const headerType = useSelector<RootState, HeaderType>((state) => state.gant.headerType);
    const startDate = useSelector<RootState, string>((state) => state.gant.startDate);
    const endDate = useSelector<RootState, string>((state) => state.gant.endDate);
    const isDragging = useSelector<RootState, boolean>((state) => state.gantDraggable.isDragging);

    const { setUrlParam } = useRouting();

    useEffect(() => {
        if (headerType === 'day') {
            dispatch(setterGant({ modeTimeLine: 'timeline' }));
        }
    }, [headerType]);

    useEffect(() => {
        if (isDragging || widthScreenForGant < 0) {
            return;
        }

        let startDateExtend;
        let widthColumn;
        let endDateExtend = endDate;

        const currentStartDate = DateTime.fromISO(startDate);
        const newStartDate =
            headerType === 'week' ? getStartOfWeek(currentStartDate) : currentStartDate.startOf(headerType);

        let countDay = WIDTH_COLUMN;

        switch (headerType) {
            case 'week':
                countDay = 7;
                break;
            case 'year':
                countDay = 365;
                break;
            case 'month':
                countDay = 31;
                break;
            case 'quarter':
                countDay = 31 * 3;
                break;
        }

        if (startDate && modeTimeLine === 'pagination') {
            widthColumn = widthScreenForGant / countDay;
            startDateExtend = newStartDate.toFormat(FORMAT_DATE);
        } else {
            widthColumn = getWidthColumn(headerType);
            startDateExtend = startDate;

            const fullLengthGantInDays = Math.floor(DateTime.fromISO(endDate).diff(currentStartDate, 'days').days);
            const lengthScreenDays = Math.floor(widthScreenForGant / widthColumn) - 1;

            if (lengthScreenDays > fullLengthGantInDays) {
                endDateExtend = currentStartDate.plus({ days: lengthScreenDays }).toFormat(FORMAT_DATE);
            }
        }

        dispatch(setterGant({ startDateExtend, widthColumn, endDateExtend }));
    }, [modeTimeLine, headerType, startDate, endDate, isDragging, widthScreenForGant]);

    useEffect(() => {
        if (startDate && modeTimeLine === 'pagination') {
            dispatch(setterGantScroll({ currentScrollX: 0 }));
        }
    }, [startDate, modeTimeLine]);

    useEffect(() => {
        dispatch(setterGant({ calendarOffsetDays: 0 }));
    }, [modeTimeLine]);

    const handleSwitch = (value: boolean) => {
        const newModeTimeLine = value ? 'timeline' : 'pagination';
        setUrlParam({ mode: newModeTimeLine });
        dispatch(setterGant({ modeTimeLine: newModeTimeLine }));
    };

    return (
        <Flex>
            <ConfigProvider
                theme={{
                    components: {
                        Switch: {
                            // handleSize: 18,
                            // trackHeight: 22,
                            // trackMinWidth: 60,
                        },
                    },
                    token: {
                        colorTextQuaternary: '#616161',
                    },
                }}
            >
                <Switch
                    checkedChildren={<Table size={20} style={{ marginTop: -2, marginRight: 5 }} />}
                    unCheckedChildren={<TableSplit size={20} style={{ marginTop: -2, marginLeft: 2 }} />}
                    value={modeTimeLine === 'timeline'}
                    disabled={headerType === 'day'}
                    onChange={handleSwitch}
                />
            </ConfigProvider>
        </Flex>
    );
};

export default SwitchTimeLine;
